import { createFileRoute, Link } from '@tanstack/react-router'
import { contentWrapper } from '../../../../styles/contentWrapper';
import { pageTitle, sectionWrapper, titleWrapper } from '../../../../styles/sectionStyles';
import ArrowBack from '~icons/material-symbols/arrow-back.svg'
import { css } from '../../../../../styled-system/css';
import { button as buttonCva } from '../../../../styles/buttonStyles';
import {
  type buttonVariants,
  createNotificationRequestSchema,
  defaultPushNotificationConfig
} from '../../../../libs/admin/schemas/create-notification';
import type { z } from 'zod';
import { atom, Provider, useAtom, useAtomValue } from 'jotai';
import { useState } from 'react';
import { BaseDialog } from '../../../../components/dialog';
import { useCreateNotification, useGetUser } from '../../../../libs/admin/hooks';
import { transformNotificationRequest } from '../../../../libs/utils/transform-notification-request';
import { NotificationPreview } from '../../../../components/notification-preview';
import { NotificationContentForm } from '../../../../components/notification-content-form';
import { LoadingOverlay } from '../../../../components/loading-overlay';
import { inputLabel, inputWrapper } from '../../../../styles/inputStyles';

export const Route = createFileRoute(
  '/admin/users/$subscriptionOrUserId/createNotification',
)({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <Provider>
      <Layout />
    </Provider>
  );
}

const Layout = () => {
  const dirtyInfo = useAtomValue(dirtyInfoAtom);

  return (
    <div className={contentWrapper()}>
      <Title />
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <BreadCrumbs />
        <div className={css({
          display: 'flex',
          gap: '32px',
        })}>
          <Content />
          <NotificationPreview
            title={dirtyInfo.titleTemplate}
            message={dirtyInfo.messageTemplate}
            button={dirtyInfo.buttonTemplate.text}
            buttonVariant={dirtyInfo.buttonTemplate.variant}
            url={dirtyInfo.serviceUrlTemplate}
          />
        </div>
      </div>
    </div>
  )
}

const Title = () => {
  const { subscriptionOrUserId } = Route.useParams();
  return (
    <div className={titleWrapper()}>
      <h1 className={pageTitle()}>
        <Link to='/admin/users/$subscriptionOrUserId' params={{ subscriptionOrUserId }}>
          <ArrowBack />
        </Link>
        プッシュ通知作成
      </h1>
    </div>
  );
}

type DirtyPushNotificationInformation = Omit<z.infer<typeof createNotificationRequestSchema>, 'userId'>
type ButtonVariant = z.infer<typeof buttonVariants>;
const dirtyInfoAtom = atom<DirtyPushNotificationInformation>(defaultPushNotificationConfig);

const BreadCrumbs = () => {
  const { subscriptionOrUserId } = Route.useParams();
  const { data: userData, isLoading } = useGetUser({ subscriptionOrUserId });

  if (isLoading) {
    return null;
  }

  if (!userData?.user) {
    return null;
  }

  return (
    <div className={css({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    })}>
      <Link to="/admin/users">
        <span className={css({
          color: 'text.accentPrimary',
          fontWeight: '400',
        })}>
          利用者検索
        </span>
      </Link>
      <span className={css({
        color: 'text.tertiary',
      })}>/</span>
      <Link to={"/admin/users/$subscriptionOrUserId"} params={{ subscriptionOrUserId }}>
        <span className={css({
          color: 'text.accentPrimary',
          fontWeight: '400',
        })}>{userData.user.name || "--"}</span>
      </Link>
      <span className={css({
        color: 'text.tertiary',
      })}>/</span>
      <span className={css({
        color: 'text.tertiary',
      })}>プッシュ通知</span>
    </div>
  );
};

const Content = () => {
  const { subscriptionOrUserId } = Route.useParams();
  const { data: userData, isLoading: isUserLoading } = useGetUser({ subscriptionOrUserId });
  const userId = userData?.user?.id;

  const [dirtyInfo, setDirtyInfo] = useAtom(dirtyInfoAtom);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const createNotification = useCreateNotification();

  const fullData = userId ? { ...dirtyInfo, userId } : dirtyInfo;
  const parsed = createNotificationRequestSchema.safeParse(fullData);
  const errors = parsed.error?.format();

  const navigate = Route.useNavigate();

  const handleConfirm = async () => {
    if (parsed.success) {
      try {
        const data = transformNotificationRequest(parsed.data);
        await createNotification.mutateAsync(data);
        setShowConfirmDialog(false);
        navigate({
          to: '/admin/users/$subscriptionOrUserId',
          params: { subscriptionOrUserId },
        });
      } catch (error) {
        console.error('Failed to send notification:', error);
      }
    }
  };

  if (isUserLoading) {
    return <LoadingOverlay />;
  }

  if (!userId) {
    return <div>ユーザーが見つかりません</div>;
  }

  return (
    <>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: '100%',
      })}>
        <div className={css(
          sectionWrapper.raw(),
          {
            backgroundColor: 'transparent',
          }
        )}>
          <div className={inputWrapper()}>
            <h2 className={inputLabel()}>送信対象</h2>
            <p>{userData?.user?.name}</p>
          </div>
        </div>
        <div className={sectionWrapper()}>
          {/* 通知内容 */}
          <NotificationContentForm
            titleTemplateValue={dirtyInfo.titleTemplate}
            onTitleTemplateChange={(value) => {
              setDirtyInfo((prev) => ({
                ...prev,
                titleTemplate: value,
              }))
            }}
            isTitleTemplateValid={errors?.titleTemplate === undefined}
            titleTemplateValidationMessage={errors?.titleTemplate ? errors.titleTemplate._errors[0] : undefined}

            messageTemplateValue={dirtyInfo.messageTemplate}
            onMessageTemplateChange={(value) => {
              setDirtyInfo((prev) => ({
                ...prev,
                messageTemplate: value,
              }))
            }}
            isMessageTemplateValid={errors?.messageTemplate === undefined}
            messageTemplateValidationMessage={errors?.messageTemplate ? errors.messageTemplate._errors[0] : undefined}

            buttonVariantValue={dirtyInfo.buttonTemplate.variant}
            onButtonVariantChange={(value) => {
              setDirtyInfo((prev) => ({
                ...prev,
                buttonTemplate: {
                  ...prev.buttonTemplate,
                  variant: value as ButtonVariant,
                }
              }))
            }}
            isButtonVariantValid={errors?.buttonTemplate?.variant === undefined}

            buttonTextValue={dirtyInfo.buttonTemplate.text}
            onButtonTextChange={(value) => {
              setDirtyInfo((prev) => ({
                ...prev,
                buttonTemplate: {
                  ...prev.buttonTemplate,
                  text: value,
                }
              }))
            }}
            isButtonTextValid={errors?.buttonTemplate?.text === undefined}
            buttonTextValidationMessage={errors?.buttonTemplate?.text ? errors.buttonTemplate.text._errors[0] : undefined}

            buttonServiceIdValue={dirtyInfo.buttonTemplate.serviceId || ''}
            onButtonServiceIdChange={(value) => {
              setDirtyInfo((prev) => ({
                ...prev,
                buttonTemplate: {
                  ...prev.buttonTemplate,
                  serviceId: value
                }
              }))
            }}

            buttonHrefValue={dirtyInfo.buttonTemplate.href}
            onButtonHrefChange={(value) => {
              setDirtyInfo((prev) => ({
                ...prev,
                buttonTemplate: {
                  ...prev.buttonTemplate,
                  href: value,
                }
              }))
            }}
            isButtonHrefValid={errors?.buttonTemplate?.href === undefined}
            buttonHrefValidationMessage={errors?.buttonTemplate?.href ? errors.buttonTemplate.href._errors[0] : undefined}

            serviceUrlTemplateValue={dirtyInfo.serviceUrlTemplate || ''}
            onServiceUrlTemplateChange={(value) => {
              setDirtyInfo((prev) => ({
                ...prev,
                serviceUrlTemplate: value
              }))
            }}
            isServiceUrlTemplateValid={errors?.serviceUrlTemplate === undefined}
            serviceUrlTemplateValidationMessage={errors?.serviceUrlTemplate ? errors.serviceUrlTemplate._errors[0] : undefined}
          />
        </div>
        <button
          className={buttonCva({ color: 'primary' })}
          type='button'
          onClick={() => setShowConfirmDialog(true)}
          disabled={!parsed.success}
        >
          送信する
        </button>
      </div>

      <BaseDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title="プッシュ通知を送信しますか？"
      >
        <div className={css({
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          marginTop: '24px',
        })}>
          <button
            className={buttonCva()}
            type='button'
            onClick={() => setShowConfirmDialog(false)}
          >
            キャンセル
          </button>
          <button
            className={buttonCva({ color: 'primary' })}
            type='button'
            onClick={handleConfirm}
            disabled={createNotification.isPending}
          >
            送信する
          </button>
        </div>
      </BaseDialog>
    </>
  )
}
